import { IconBadge } from '@skyline-platform/alchemy';

/**
 * Set any null or undefined fields to empty strings
 * @param {*} obj
 * @param {*} stringFields
 * @returns
 */
export const normalizeStringFields = (obj, stringFields) => {
  const newObj = { ...obj };
  stringFields.forEach((field) => {
    if (newObj[field] === null || newObj[field] === undefined) {
      newObj[field] = '';
    }
  });
  return newObj;
};

export const renderBadgeForPermission = (permission) => (
  <IconBadge
    label={`API Key Permission ${permission ? 'Can' : 'Cannot'}`}
    iconClassName={permission ? 'fa-solid fa-check' : 'fa-solid fa-xmark'}
    iconStyle={{ color: permission ? 'green' : 'red' }}
    srText={permission ? 'Yes' : 'No'}
    className='permission-badge'
  />
);

export const getContactLabelAndName = (contact) => {
  const fullName = `${contact.first_name} ${contact.last_name}`;

  return {
    label: `${fullName} | ${contact.email_address}`,
    name: fullName,
  };
};

/**
 * Adds the management addresss or network address to
 *  the search existing appliance or camera input, respectively
 * @param {*} obj
 * @param {*} addressKey
 * @returns
 */
export const getAddrLabel = (obj, addressKey) => {
  if (!obj) return '';
  const { name = '', [addressKey]: address } = obj;
  return address ? `${name} | ${address}` : name;
};

export const getStreamProfileLabel = (streamProf) => {
  if (!streamProf) return '';

  const {
    name, appliance, device,
  } = streamProf;

  // if stream prof source is appliance - show mgmt addr
  if (appliance?.management_address) {
    return `${name} | ${appliance.management_address}`;
  }
  // if stream prof source is camera - show network addr
  if (device?.network_address) {
    return `${name} | ${device.network_address}`;
  }
  return name;
};

/**
 * Builds a server-side search criteria object from a search term, a list of
 * active frontend field names, and a map of frontend field names to backend query keys.
 * Trims leading and trailing whitespace from the search term before processing.
 * @param {string} searchTerm - The raw search input from the user.
 * @param {string[]} fields - The frontend field names currently selected for search.
 * @param {Object} fieldMap - A mapping of frontend field names to backend query keys.
 * @returns {Object} A criteria object suitable for passing to a backend query,
 *   or an empty object if no valid criteria can be built.
 */
export const buildSearchCriteria = (searchTerm, fields, fieldMap) => {
  const trimmed = searchTerm?.trim();
  if (!trimmed) return {};
  const serverFields = fields.filter((f) => fieldMap[f]);
  if (serverFields.length === 0) return {};
  // Escape regex special characters so literal input like "Rd (S)" is not
  // interpreted as a regex group by the API.
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (serverFields.length === 1) {
    return { [fieldMap[serverFields[0]]]: `regex:${escaped}` };
  }
  return {
    or: serverFields.map((f) => ({ [fieldMap[f]]: `regex:${escaped}` })),
  };
};

/**
 * Combines search criteria and column-filter criteria into a single criteria object.
 * Uses the GraphQL `and` operator when both are present so the server requires
 * matches in ALL groups simultaneously.
 *
 * @param {Object} searchCriteria - Criteria from buildSearchCriteria (may contain `or` or a single field).
 * @param {Object} columnFilters - Active column filters: { field: [value1, value2, ...] }.
 * @param {Object} filterFieldMap - Maps table column field names to backend criteria keys.
 * @returns {Object} Combined criteria suitable for passing to a GraphQL creator.
 */
export const buildCombinedCriteria = (searchCriteria, columnFilters, filterFieldMap) => {
  const filterGroups = Object.entries(columnFilters || {})
    .filter(([, values]) => values.length > 0)
    .map(([field, values]) => {
      const backendKey = filterFieldMap[field] || field;
      if (values.length === 1) {
        return { [backendKey]: values[0] };
      }
      return { or: values.map((v) => ({ [backendKey]: v })) };
    });

  const hasSearch = Object.keys(searchCriteria || {}).length > 0;
  const hasFilters = filterGroups.length > 0;

  if (!hasSearch && !hasFilters) return {};
  if (hasSearch && !hasFilters) return searchCriteria;
  if (!hasSearch && hasFilters) {
    return filterGroups.length === 1 ? filterGroups[0] : { and: filterGroups };
  }

  const filterKeys = filterGroups.flatMap((g) => Object.keys(g));
  const canFlatMerge = filterGroups.every((g) => !g.or) &&
    filterKeys.every((k) => !(k in searchCriteria));

  if (canFlatMerge) return Object.assign({}, searchCriteria, ...filterGroups);

  const searchGroup = searchCriteria.or ? { or: searchCriteria.or } : searchCriteria;
  return { and: [searchGroup, ...filterGroups] };
};